import React, { useMemo } from 'react';
import { CompositeDataSetRecipe } from 'libs/end-to-end/src/types/databases/data-sets/composite-data-set-config';
import { z } from 'zod';
import { DataSetCompositeRecipeDataWizardTracker } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeRecipeDataWizardTracker';
import { DataSetCompositeRecipeDataWizardTrackerItem } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeRecipeDataWizardTracker';
import { useDataSetCompositeRecipeDataWizardTracker } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeRecipeDataWizardTracker';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { UseGenerateReportsInsightsPayload } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/use-generate-reports-insights';
import { CompositeDataSetCommuteTimesRecipe } from 'libs/end-to-end/src/types/databases/data-sets/composite-data-set-config';
import { useSimpleReadPattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadPattern';
import { uiEnvironment } from 'apps/sigma2-ui/src/constants/environment';
import { DataSets } from 'libs/end-to-end/src/types/databases/data-sets/data-sets';
import { SelectBoxWithSearch } from 'apps/sigma2-ui/src/components/ui/SelectBoxWithSearch';
import { isMergeAtsIntegrationDataSet } from 'libs/end-to-end/src/is-merge-ats-integration-data-set';
import { WorkflowDataSetSelector } from 'apps/sigma2-ui/src/components/workflows/WorkflowDataSetSelector';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { useCallback } from 'react';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';

const CommuteTimesMatchingConfigStep = z.enum([
  'select_commute_times_type',
  'column_matching',
  'final-review',
  'complete',
]);

type CommuteTimesMatchingConfigStep = z.infer<
  typeof CommuteTimesMatchingConfigStep
>;

export const DataSetCompositeRecipeCommuteTimesConfigComponent = ({
  config,
  setConfig,
  insightsPayload,
  organizationId,
  databaseId,
}: {
  config: CompositeDataSetCommuteTimesRecipe;
  setConfig: (config: CompositeDataSetRecipe) => void;
  insightsPayload: UseGenerateReportsInsightsPayload;
  organizationId: string;
  databaseId: string;
}) => {
  const language = useLanguage();

  const stepItems: DataSetCompositeRecipeDataWizardTrackerItem<CommuteTimesMatchingConfigStep>[] =
    useMemo(() => {
      return [
        {
          value: 'select_commute_times_type',
          display: 'Select Commute Times Type',
        },
      ];
    }, []);

  const patternListDataSetsGetUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}/data-sets`;
  }, [organizationId, databaseId]);

  const listDataSetsPattern = useSimpleReadPattern({
    responseSchema: DataSets,
    canRead: true,
    getUrlFn: patternListDataSetsGetUrlFn,
  });

  return (
    <Spacing size={'lg'} direction={'vertical'}>
      <SelectBoxWithSearch
        type="single"
        list={['Employee', 'Candidate'].map((e) => ({
          optionLabel: e,
          optionValue: e,
        }))}
        selected={config.commuteTimesType ?? 'Candidate'}
        label={'Select Commute Times Type'}
        setSelected={(e) => {
          setConfig({
            ...config,
            commuteTimesType: e as 'Employee' | 'Candidate',
          });
        }}
      />
      <WorkflowDataSetSelector
        title={'Merge Dataset'}
        dataSetId={config.mergeDataSetId}
        setDataSetId={(id: string) => {
          setConfig({
            ...config,
            mergeDataSetId: id,
          });
        }}
        listDataSetsPattern={listDataSetsPattern}
        filterFn={isMergeAtsIntegrationDataSet}
      />
    </Spacing>
  );
};
